import React, { useState } from 'react';
import type { Assessment } from '../../types/form.types';
import { useTheme } from '@theme/useTheme';
import dayjs from 'dayjs';

interface AssessmentHistoryCardsProps {
  assessments: Assessment[] | null;
}

const AssessmentHistoryCards: React.FC<AssessmentHistoryCardsProps> = ({ assessments }) => {
  const { colors } = useTheme();
  const [expandedCard, setExpandedCard] = useState<number | null>(0); // First card expanded by default

  const getRiskLevelColor = (riskLevel: string): string => {
    switch (riskLevel.toLowerCase()) {
      case "low":
        return "#10B981"; // Green
      case "moderate":
        return "#F59E0B"; // Orange
      case "high":
        return "#EF4444"; // Red
      default:
        return "#6B7280"; // Gray
    }
  };

  const formatDate = (date: string | Date): string => {
    return dayjs(date).format('MMM DD, YYYY [at] h:mm A');
  };

  const toggleCard = (index: number) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  if (!assessments || assessments.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 shadow-lg border"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border
        }}
      >
        <h2
          className="text-2xl font-bold mb-4 text-center"
          style={{ color: colors.heading }}
        >
          Assessment History
        </h2>
        <div className="text-center py-12">
          <p
            className="text-lg"
            style={{ color: colors.text }}
          >
            No assessments available
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-tr-3xl rounded-bl-3xl border p-4 shadow-lg"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.border
      }}
    >
      <div className="text-center mb-4">
        <h2
          className="text-xl font-bold mb-1"
          style={{ color: colors.heading }}
        >
          Assessment History
        </h2>
        <p
          className="text-xs font-medium"
          style={{ color: colors.text }}
        >
          {assessments.length} assessment{assessments.length !== 1 ? "s" : ""} recorded
        </p>
      </div>

      <div className="space-y-3 max-h-80 overflow-y-auto">
        {assessments.map((assessment, index) => {
          const isExpanded = expandedCard === index;
          const isFirstCard = index === 0;

          return (
            <div
              key={index}
              className="rounded-tr-xl rounded-bl-xl border p-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
              style={{
                backgroundColor: colors.input,
                borderColor: colors.border
              }}
            >
              {/* Card Header */}
              <div className="flex justify-between items-center mb-2">
                <h3
                  className="text-sm font-bold"
                  style={{ color: colors.heading }}
                >
                  {isFirstCard ? 'Current Assessment' : `Assessment #${assessments.length - index}`}
                </h3>
                <button
                  onClick={() => toggleCard(index)}
                  className="p-1 rounded-full transition-colors duration-200"
                  style={{
                    color: colors.text,
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.secondaryButton;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {isExpanded ? (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Card Content */}
              <div className="space-y-2">
                {/* Risk Level Badge */}
                <div className="flex items-center justify-between">
                  <span
                    className="inline-block px-2 py-1 rounded-full text-white text-xs font-bold uppercase tracking-wide"
                    style={{ backgroundColor: getRiskLevelColor(assessment.risk_level) }}
                  >
                    {assessment.risk_level}
                  </span>

                  {/* Date */}
                  <span
                    className="text-xs font-medium"
                    style={{ color: colors.text }}
                  >
                    {formatDate(assessment.taken_at)}
                  </span>
                </div>

                {/* Risk Score */}
                <div className="flex items-center justify-between">
                  <div>
                    <span
                      className="text-xs font-semibold uppercase tracking-wide block mb-1"
                      style={{ color: colors.text }}
                    >
                      Risk Score
                    </span>
                    <span
                      className="text-2xl font-bold"
                      style={{ color: getRiskLevelColor(assessment.risk_level) }}
                    >
                      {assessment.risk_score}%
                    </span>
                  </div>

                  {/* Expandable Details */}
                  {isExpanded && (
                    <div className="text-right">
                      <div className="space-y-1">
                        <div>
                          <span
                            className="text-xs font-semibold uppercase tracking-wide block"
                            style={{ color: colors.text }}
                          >
                            Risk Level
                          </span>
                          <span
                            className="inline-block px-2 py-1 rounded-full text-white text-xs font-bold uppercase tracking-wide mt-1"
                            style={{ backgroundColor: getRiskLevelColor(assessment.risk_level) }}
                          >
                            {assessment.risk_level}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AssessmentHistoryCards;
